import React, { useMemo } from "react";
import { Box, Grid } from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import TouchAppIcon from "@mui/icons-material/TouchApp";
import PeopleIcon from "@mui/icons-material/People";
import ThumbUpIcon from "@mui/icons-material/ThumbUp";
import PhotoLibraryIcon from "@mui/icons-material/PhotoLibrary";
import {
  StatCard,
  CardHeading,
  CardSecondaryText,
  cardStatIconSx,
  formatNumber,
} from "./MediaKitComponents";
import { MediaKitAnalyticsLayout } from "./MediaKitAnalyticsLayout";
import { MediaKitDemographyTextBlock } from "./MediaKitDemographyTextBlock";
import type { PodcastMediaKit as PodcastMediaKitType } from "../../api/podcasts";
import { useTranslation } from "react-i18next";
import { MediaKitMetricsWindowCaption } from "./MediaKitMetricsWindowCaption";

const iconSx = cardStatIconSx(30);

interface MediaKitInstagramAnalyticsSectionProps {
  analytics: PodcastMediaKitType["analytics"];
  platformUrl?: string | null;
  contentCount?: number;
}

export const MediaKitInstagramAnalyticsSection: React.FC<
  MediaKitInstagramAnalyticsSectionProps
> = ({ analytics, contentCount }) => {
  const { t } = useTranslation("mediaKit");
  const reach = analytics?.instagram?.reach;

  const reachTotalReach = useMemo(() => {
    if (!reach?.totalReach) return null;
    return (
      <Grid size={{ xs: 12, sm: 6, md: 4 }}>
        <StatCard>
          <PeopleIcon sx={iconSx} />
          <CardHeading variant="h5">
            {formatNumber(reach.totalReach)}
          </CardHeading>
          <CardSecondaryText variant="body2">{t("metrics.reach")}</CardSecondaryText>
        </StatCard>
      </Grid>
    );
  }, [reach?.totalReach, t]);

  const reachAvgReach = useMemo(() => {
    if (!reach?.avgReach) return null;
    return (
      <Grid size={{ xs: 12, sm: 6, md: 4 }}>
        <StatCard>
          <PeopleIcon sx={iconSx} />
          <CardHeading variant="h5">{formatNumber(reach.avgReach)}</CardHeading>
          <CardSecondaryText variant="body2">
            {t("metrics.avgReach")}
          </CardSecondaryText>
        </StatCard>
      </Grid>
    );
  }, [reach?.avgReach, t]);

  const reachTotalViews = useMemo(() => {
    if (!reach?.totalViews) return null;
    return (
      <Grid size={{ xs: 12, sm: 6, md: 4 }}>
        <StatCard>
          <VisibilityIcon sx={iconSx} />
          <CardHeading variant="h5">
            {formatNumber(reach.totalViews)}
          </CardHeading>
          <CardSecondaryText variant="body2">{t("metrics.totalViews")}</CardSecondaryText>
        </StatCard>
      </Grid>
    );
  }, [reach?.totalViews, t]);

  const reachAvgViews = useMemo(() => {
    if (!reach?.avgViews) return null;
    return (
      <Grid size={{ xs: 12, sm: 6, md: 4 }}>
        <StatCard>
          <VisibilityIcon sx={iconSx} />
          <CardHeading variant="h5">{formatNumber(reach.avgViews)}</CardHeading>
          <CardSecondaryText variant="body2">{t("metrics.avgViews")}</CardSecondaryText>
        </StatCard>
      </Grid>
    );
  }, [reach?.avgViews, t]);

  const reachTotalInteractions = useMemo(() => {
    if (!reach?.totalInteractions) return null;
    return (
      <Grid size={{ xs: 12, sm: 6, md: 4 }}>
        <StatCard>
          <TouchAppIcon sx={iconSx} />
          <CardHeading variant="h5">
            {formatNumber(reach.totalInteractions)}
          </CardHeading>
          <CardSecondaryText variant="body2">
            {t("metrics.totalInteractions")}
          </CardSecondaryText>
        </StatCard>
      </Grid>
    );
  }, [reach?.totalInteractions, t]);

  const reachAvgInteractions = useMemo(() => {
    if (!reach?.avgInteractions) return null;
    return (
      <Grid size={{ xs: 12, sm: 6, md: 4 }}>
        <StatCard>
          <TouchAppIcon sx={iconSx} />
          <CardHeading variant="h5">
            {formatNumber(reach.avgInteractions)}
          </CardHeading>
          <CardSecondaryText variant="body2">
            {t("metrics.avgInteractions")}
          </CardSecondaryText>
        </StatCard>
      </Grid>
    );
  }, [reach?.avgInteractions, t]);

  const engagementRate = useMemo(() => {
    if (!analytics?.instagram?.engagement?.engagementRate) return null;
    return (
      <Grid size={{ xs: 12, sm: 6, md: 4 }}>
        <StatCard>
          <ThumbUpIcon sx={iconSx} />
          <CardHeading variant="h5">
            {analytics.instagram.engagement.engagementRate.toFixed(2)}%
          </CardHeading>
          <CardSecondaryText variant="body2">
            {t("metrics.engagementRate")}
          </CardSecondaryText>
        </StatCard>
      </Grid>
    );
  }, [analytics?.instagram?.engagement?.engagementRate, t]);

  if (!analytics?.instagram) {
    return null;
  }

  const reachHasAlcanceOnly =
    reach &&
    (reach.totalReach != null ||
      reach.avgReach != null ||
      reach.totalViews != null ||
      reach.avgViews != null);
  const hasInstagramEngagementSection =
    analytics.instagram.engagement?.engagementRate != null ||
    reach?.totalInteractions != null ||
    reach?.avgInteractions != null;

  return (
    <MediaKitAnalyticsLayout>
      <Box sx={{ display: "flex", flexDirection: "column" }}>
        {(reachHasAlcanceOnly || contentCount != null) && (
          <Box component="section" data-mediakit-pdf-section="" sx={{ pb: 4 }}>
            <CardHeading variant="h5" sx={{ mb: 2 }}>
              {t("metrics.reach")}
            </CardHeading>
            <Grid container spacing={2}>
              {contentCount != null && (
                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                  <StatCard>
                    <PhotoLibraryIcon sx={iconSx} />
                    <CardHeading variant="h5">{contentCount}</CardHeading>
                    <CardSecondaryText variant="body2">Posts</CardSecondaryText>
                  </StatCard>
                </Grid>
              )}
              {reachTotalReach}
              {reachAvgReach}
              {reachTotalViews}
              {reachAvgViews}
            </Grid>
            <MediaKitMetricsWindowCaption
              format="shorts"
              inProgressCount={reach?.inProgressCount}
            />
          </Box>
        )}

        {hasInstagramEngagementSection && (
          <Box component="section" data-mediakit-pdf-section="" sx={{ pb: 4 }}>
            <CardHeading variant="h5" sx={{ mb: 2 }}>
              Engagement
            </CardHeading>
            <Grid container spacing={2}>
              {reachTotalInteractions}
              {reachAvgInteractions}
              {engagementRate}
            </Grid>
          </Box>
        )}
      </Box>

      {analytics.instagram.audience && (
        <Box component="section" data-mediakit-pdf-section="" sx={{ pb: 4 }}>
          <MediaKitDemographyTextBlock
            gender={analytics.instagram.audience.gender}
            ageRanges={analytics.instagram.audience.ageRanges}
          />
        </Box>
      )}
    </MediaKitAnalyticsLayout>
  );
};
